"use client"

import { useEffect } from "react"
import { usePathname } from "next/navigation"
import posthog from "posthog-js"

// Delegated hover analytics: any element carrying data-track-hover="<name>"
// gets a custom `element_hover` PostHog event once the pointer has dwelled on
// it for HOVER_DWELL_MS (drive-by passes don't count), at most once per name
// per page visit — so event volume is bounded by the number of instrumented
// elements, not by pointer traffic. The /analytics dashboard groups these
// events by their hover_id property. To instrument something new, just add
// the attribute; there are no per-component listeners.
const HOVER_DWELL_MS = 500

export const HoverTracking = () => {
  const pathname = usePathname()

  useEffect(() => {
    const seen = new Set<string>()
    let pendingName: string | null = null
    let timer: number | null = null

    const cancelPending = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = null
      pendingName = null
    }

    const nameAt = (node: EventTarget | null) =>
      node instanceof Element
        ? node.closest("[data-track-hover]")?.getAttribute("data-track-hover") ?? null
        : null

    const handleMouseOver = (event: MouseEvent) => {
      const name = nameAt(event.target)
      // Moving between children of the same tracked element re-fires
      // mouseover with the same name — keep the dwell timer running.
      if (name === pendingName) return
      cancelPending()
      if (!name || seen.has(name)) return
      pendingName = name
      timer = window.setTimeout(() => {
        seen.add(name)
        pendingName = null
        timer = null
        posthog.capture("element_hover", { hover_id: name, pathname })
      }, HOVER_DWELL_MS)
    }

    // mouseout catches the case mouseover can't: the pointer leaving the
    // window (or settling on nothing) mid-dwell, where no new mouseover ever
    // fires to cancel the pending timer.
    const handleMouseOut = (event: MouseEvent) => {
      if (pendingName === null) return
      if (nameAt(event.target) !== pendingName) return
      if (nameAt(event.relatedTarget) === pendingName) return
      cancelPending()
    }

    document.addEventListener("mouseover", handleMouseOver)
    document.addEventListener("mouseout", handleMouseOut)
    return () => {
      cancelPending()
      document.removeEventListener("mouseover", handleMouseOver)
      document.removeEventListener("mouseout", handleMouseOut)
    }
  }, [pathname])

  return null
}
